import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Plus, Minus, Trash2, X, Sparkles, LogOut, Loader2, Radio } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function ClientCatalog() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("Tout");
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const cart = useCart();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const location = useLocation();
  const [active, setActive] = useState([]);
  const [checking, setChecking] = useState(!location.state?.browse);

  useEffect(() => {
    const browse = location.state?.browse;
    if (browse) window.history.replaceState({ ...window.history.state, usr: undefined }, "");
    api.get("/orders/active").then(({ data }) => {
      setActive(data);
      if (browse || data.length === 0) { setChecking(false); return; }
      navigate(data.length === 1 ? `/order/${data[0].id}` : "/orders", { replace: true });
    }).catch(() => setChecking(false));
  }, [location.state, navigate]);

  useEffect(() => {
    let cancelled = false;
    const loadCatalog = async (attempt = 0) => {
      try {
        const { data } = await api.get("/products");
        if (cancelled) return;
        setProducts(data);
        const cats = Array.from(new Set(data.map((p) => p.category))).sort();
        setCategories(["Tout", ...cats]);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        if (attempt < 2) {
          setTimeout(() => loadCatalog(attempt + 1), 1500);
        } else {
          setLoading(false);
          toast.error("Impossible de charger le catalogue. Vérifiez votre connexion.");
        }
      }
    };
    loadCatalog();
    return () => { cancelled = true; };
  }, []);

  if (checking) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-amber-400 animate-spin" /></div>;

  const filtered = activeCat === "Tout" ? products : products.filter((p) => p.category === activeCat);

  return (
    <div className="max-w-md mx-auto min-h-screen bg-[#08080C] pb-28 border-x border-amber-500/10 relative">
      {/* Header */}
      <header className="glass sticky top-0 z-30 px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg">
            <Sparkles className="w-4 h-4 text-slate-950" />
          </div>
          <div>
            <h1 className="text-xl leading-none gold-text font-bold">Avenue Benda</h1>
            <p className="text-[10px] font-mono uppercase tracking-widest text-amber-400/60">Service de Livraison</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="open-cart-button" onClick={() => setCartOpen(true)}
            className="relative w-11 h-11 rounded-full glass flex items-center justify-center card-hover">
            <ShoppingBag className="w-5 h-5 text-amber-400" />
            {cart.count > 0 && (
              <span data-testid="cart-count" className="absolute -top-1 -right-1 bg-amber-500 text-slate-950 text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {cart.count}
              </span>
            )}
          </button>
          <button data-testid="client-logout" onClick={async () => { await logout(); navigate("/login"); }}
            className="w-11 h-11 rounded-full glass flex items-center justify-center text-slate-300 card-hover">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {active.length > 0 && (
        <button data-testid="active-orders-banner" onClick={() => navigate(active.length === 1 ? `/order/${active[0].id}` : "/orders")}
          className="mx-5 mt-4 w-[calc(100%-2.5rem)] rounded-2xl border border-amber-500/50 bg-amber-500/10 px-4 py-3 flex items-center gap-3 text-left card-hover">
          <Radio className="w-5 h-5 text-amber-400 animate-pulse shrink-0" />
          <span className="flex-1 text-sm font-semibold text-amber-100">
            {active.length === 1 ? `Suivre ma commande ${active[0].order_number}` : `Suivre mes ${active.length} commandes en cours`}
          </span>
          <span className="text-xs font-mono text-amber-400">Suivi →</span>
        </button>
      )}

      {/* Hero */}
      <div className="px-5 pt-6 pb-2 fade-up">
        <h2 className="text-3xl font-bold text-slate-100 leading-tight">Livraison nocturne <span className="gold-text">d'exception</span></h2>
        <p className="text-sm text-slate-400 mt-1">Sélection premium livrée en toute discrétion.</p>
      </div>

      {/* Category tabs */}
      <div className="px-5 py-4 flex gap-2 overflow-x-auto no-scrollbar">
        {categories.map((c) => (
          <button key={c} data-testid={`category-${c}`} onClick={() => setActiveCat(c)}
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all ${
              activeCat === c ? "btn-gold" : "glass text-slate-300 hover:text-amber-300"}`}>
            {c}
          </button>
        ))}
      </div>

      {/* Products */}
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-amber-400 animate-spin" /></div>
      ) : (
        <div className="px-5 grid grid-cols-2 gap-4">
          {filtered.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="card-hover rounded-2xl overflow-hidden bg-[#161926] border border-amber-500/15">
              <div className="aspect-square overflow-hidden bg-slate-900">
                <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-3">
                <p className="text-[10px] font-mono uppercase tracking-wider text-amber-400/70">{p.category}</p>
                <h3 className="font-sans text-sm font-semibold text-slate-100 leading-snug mt-0.5 line-clamp-2 min-h-[2.5rem]">{p.name}</h3>
                <div className="flex items-center justify-between mt-2">
                  <span className="font-mono text-amber-400 font-bold">{p.price.toFixed(2)} €</span>
                  <button data-testid={`add-to-cart-button-${p.id}`} onClick={() => { cart.add(p); toast.success(`${p.name} ajouté`); }}
                    className="w-8 h-8 rounded-full btn-gold flex items-center justify-center">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Sticky cart bar */}
      <AnimatePresence>
        {cart.count > 0 && !cartOpen && (
          <motion.div initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }}
            className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md px-4 pb-4 z-30">
            <button data-testid="view-cart-bar" onClick={() => setCartOpen(true)}
              className="w-full btn-gold rounded-2xl py-4 px-5 flex items-center justify-between">
              <span className="flex items-center gap-2 font-bold"><ShoppingBag className="w-5 h-5" /> {cart.count} article(s)</span>
              <span className="font-mono font-bold">{cart.total.toFixed(2)} €</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cart sheet */}
      <Sheet open={cartOpen} onOpenChange={setCartOpen}>
        <SheetContent side="bottom" className="max-w-md mx-auto rounded-t-3xl bg-[#10121B] border-amber-500/20 max-h-[85vh] overflow-y-auto">
          <SheetHeader><SheetTitle className="gold-text text-2xl font-bold text-left">Votre panier</SheetTitle></SheetHeader>
          {cart.items.length === 0 ? (
            <p className="text-slate-400 py-10 text-center">Panier vide</p>
          ) : (
            <div className="space-y-3 mt-4">
              {cart.items.map((i) => (
                <div key={i.product_id} className="flex items-center gap-3 bg-[#161926] rounded-xl p-2 border border-amber-500/10">
                  <img src={i.image_url} alt={i.name} className="w-14 h-14 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-100 truncate">{i.name}</p>
                    <p className="text-amber-400 font-mono text-sm">{i.price.toFixed(2)} €</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid={`cart-decrease-${i.product_id}`} onClick={() => cart.setQty(i.product_id, i.quantity - 1)} className="w-7 h-7 rounded-full glass flex items-center justify-center text-amber-300"><Minus className="w-3.5 h-3.5" /></button>
                    <span className="w-5 text-center text-slate-100 font-mono">{i.quantity}</span>
                    <button data-testid={`cart-increase-${i.product_id}`} onClick={() => cart.setQty(i.product_id, i.quantity + 1)} className="w-7 h-7 rounded-full glass flex items-center justify-center text-amber-300"><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <button onClick={() => cart.remove(i.product_id)} className="text-rose-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <div className="flex justify-between items-center pt-3 border-t border-amber-500/15">
                <span className="text-slate-300">Total</span>
                <span className="text-2xl font-bold gold-text font-mono">{cart.total.toFixed(2)} €</span>
              </div>
              <Button data-testid="checkout-button" onClick={() => { setCartOpen(false); setCheckoutOpen(true); }}
                className="w-full btn-gold rounded-xl py-6 text-base">Commander</Button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <CheckoutDialog open={checkoutOpen} onOpenChange={setCheckoutOpen} />
    </div>
  );
}

function CheckoutDialog({ open, onOpenChange }) {
  const cart = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({ delivery_note: "" });
  const [address, setAddress] = useState(null);
  const [config, setConfig] = useState({ departments: [], card_fee_percent: 6 });
  const [fee, setFee] = useState(0);
  const [payment, setPayment] = useState("cash");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/delivery/config").then((r) => setConfig(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!address) { setFee(0); return; }
    let stale = false;
    api.get("/delivery/fee", { params: { postcode: address.postcode, city: address.city } })
      .then((r) => { if (!stale) setFee(r.data.fee); }).catch(() => { if (!stale) setFee(0); });
    return () => { stale = true; };
  }, [address]);

  const base = cart.total + fee;
  const cardFee = payment === "card" && config.card_fee_percent > 0
    ? Math.round((Math.ceil(Math.round(base * (100 + config.card_fee_percent)) / 100 - 1e-9) - base) * 100) / 100 : 0;

  const submit = async () => {
    if (!address) { toast.error("Choisissez votre adresse dans la liste proposée"); return; }
    setSubmitting(true);
    try {
      const items = cart.items.map((i) => ({ product_id: i.product_id, quantity: i.quantity }));
      const { data: order } = await api.post("/orders", { ...form, delivery_address: address.label, delivery_postcode: address.postcode, delivery_city: address.city, items, payment_method: payment });
      if (payment === "card") {
        const { data } = await api.post("/payments/checkout", { order_id: order.id, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
      } else {
        cart.clear();
        toast.success("Commande validée !");
        navigate(`/order/${order.id}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md mx-auto bg-[#10121B] border-amber-500/20 max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="gold-text text-2xl font-bold">Finaliser la commande</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-slate-300 text-xs">Adresse de livraison *</Label>
            <AddressAutocomplete departments={config.departments} selected={address} onSelect={setAddress} />
            <p className="text-[11px] text-slate-500 mt-1">{address ? "Adresse validée." : "Tapez votre adresse puis choisissez-la dans la liste."}</p>
          </div>
          <div>
            <Label className="text-slate-300 text-xs">Instructions (optionnel)</Label>
            <Input data-testid="checkout-note" value={form.delivery_note}
              onChange={(e) => setForm({ ...form, delivery_note: e.target.value })}
              placeholder="Bâtiment, étage, code portail…" className="bg-[#161926] border-amber-500/20 mt-1 text-slate-100" />
          </div>

          <div>
            <Label className="text-slate-300 text-xs mb-2 block">Mode de paiement</Label>
            <div className="grid grid-cols-2 gap-3">
              <button data-testid="payment-cash" onClick={() => setPayment("cash")}
                className={`p-3 rounded-xl border text-left transition-all ${payment === "cash" ? "border-amber-500 bg-amber-500/10" : "border-amber-500/15 bg-[#161926]"}`}>
                <p className="text-sm font-semibold text-slate-100">Espèces</p>
                <p className="text-[11px] text-slate-400">À la livraison</p>
              </button>
              <button data-testid="payment-card" onClick={() => setPayment("card")}
                className={`p-3 rounded-xl border text-left transition-all ${payment === "card" ? "border-amber-500 bg-amber-500/10" : "border-amber-500/15 bg-[#161926]"}`}>
                <p className="text-sm font-semibold text-slate-100">Carte Bancaire</p>
                <p className="text-[11px] text-slate-400">Paiement sécurisé Stripe</p>
              </button>
            </div>
          </div>

          <div className="flex justify-between items-center pt-2 border-t border-amber-500/15">
            <span className="text-slate-300">Sous-total</span>
            <span className="font-mono text-slate-200">{cart.total.toFixed(2)} €</span>
          </div>
          {address && (
            <div className="flex justify-between items-center">
              <span className="text-slate-300 text-sm">Frais de livraison</span>
              <span className="font-mono text-slate-200" data-testid="delivery-fee-amount">{fee > 0 ? `+${fee.toFixed(2)} €` : "Offerts"}</span>
            </div>
          )}
          {payment === "card" && (
            <div className="flex justify-between items-center text-amber-300">
              <span className="text-xs">Frais de paiement par carte</span>
              <span className="font-mono text-sm" data-testid="card-fee-amount">+{cardFee.toFixed(2)} €</span>
            </div>
          )}
          <div className="flex justify-between items-center">
            <span className="text-slate-300 font-semibold">Total à payer</span>
            <span data-testid="checkout-total" className="text-2xl font-bold gold-text font-mono">{(base + cardFee).toFixed(2)} €</span>
          </div>
          <Button data-testid="confirm-order-button" disabled={submitting || !address} onClick={submit} className="w-full btn-gold rounded-xl py-6 text-base">
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : payment === "card" ? "Payer par carte" : "Valider la commande"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
